import { useEffect, useReducer, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExtractRouteParams, generatePath, RouteComponentProps } from "react-router";
import { AUTH_URLS, createPermitURL } from "src/pages/authenticated/Routes";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import { useFeatureFlags } from "swing-components";

import { AlertError, ContentSingleColumn, QUERY_CLIENT, useAuth } from "~components";
import { InvalidConfiguration } from "~onboarding/components";
import { computeGroupsState, getConfiguration, Task } from "~onboarding/utils";
import { GET, POST, POSTBody, replacePath, translate } from "~utils";

import { PermitTaskPageBase } from "./PermitTask.page.base";
import { PermitTaskPageReducer, PermitTaskPageReducerState } from "./PermitTask.page.reducer";

/***** Types *****/
type PermitTaskPageProps = RouteComponentProps<
  ExtractRouteParams<(typeof AUTH_URLS)["permitTask"], string>
>;

/***** Component *****/
/**
 * Page | Permit Task
 *
 * Renders an array of Steps each with Questions for the Permit flow.
 */
export function PermitTaskPage(props: PermitTaskPageProps) {
  const { history, match } = props;
  const { task: taskIndex, step: stepIndex } = match.params;

  /*** Hooks ***/
  const { refreshUser, userInfo } = useAuth();
  const { flags } = useFeatureFlags();
  // NOTE: IMPORTANT we must store the Task in a Ref so that we can
  // stop re-computing which Task should be displayed.
  const taskRef = useRef<Task | undefined>(undefined);

  /*** Effects #1 - Refresh User ***/
  // Refresh the sub's details when mounting to capture any changes in
  // their `onboardingStatus` which if there is, the parent routers
  // will redirect them to the correct page.
  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*** State ***/
  const [state, dispatch] = useReducer(PermitTaskPageReducer, {
    // Setting the initial state to the URL value if it exists
    stepIndex: { initial: stepIndex ? parseInt(stepIndex) : undefined },
    // Set to true since we don't have the `taskComputed` field yet.
    isLoading: true,
    // Handlers
    onClose: handleClose,
    onSubmit: handleSubmit,
    onStepChange: handleStepChange,
  } satisfies PermitTaskPageReducerState);

  /***** Queries *****/
  // Get the Task configuration for the region
  const { data: configuration, isError: isConfigurationError } = useQuery({
    queryKey: [
      "configuration",
      userInfo.onboardingConfigurationId,
      userInfo.onboardingConfigurationId === "ca-v4" ? flags?.onboardingTask : undefined,
    ],
    queryFn: () =>
      getConfiguration(userInfo.onboardingConfigurationId || "", { featureFlags: flags }),
    // Cache the configuration for the lifetime of the application
    // since this only changes on new deployments which will require
    // an application refresh. This will make all future calls using
    // this key to not make a network request.
    staleTime: Infinity,
    // Disabling retries since the query is a lazy file import load,
    // thus if there is an error, it's on purpose.
    retry: false,
  });

  // Get the sub attributes which are used to calculate the progress of
  // each Task
  const { data: subAttributes, isError: isSubAttributesError } = useQuery({
    queryKey: ["attributes"],
    queryFn: () => GET("/api/sub/attributes"),
    // Since the response is `{ data: [...subAttribute] }` we can
    // clean up the response
    select: (data) =>
      data?.data.map((attribute) => ({
        ...attribute,
        updatedAt: new Date(attribute.updatedAt),
      })),
    // Set to 500ms (compared to 1000ms graduated backoff) to avoid
    // long waits to fail.
    retryDelay: 500,
  });

  /*** Effects #2 - Dispatches ***/
  /** When we have new Sub Attributes, then let the TaskReducer know */
  useEffect(
    () =>
      subAttributes &&
      dispatch({
        type: "SET_SUB_ATTRIBUTES",
        payload: { subAttributes },
      }),
    // Using `JSON.stringify` to compare the array of sub attributes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(subAttributes)],
  );

  /**
   * When we have a Configuration, and Sub Attributes, then compute the
   * Task
   */
  useEffect(() => {
    // When we have a Configuration, Sub Attributes and no Task
    if (configuration && subAttributes && taskRef.current == undefined) {
      // Then compute the Group
      const computedGroups = computeGroupsState({
        groups: configuration.groups,
        syncedSubAttributes: subAttributes,
      });

      // And determine the Task to display - filter for optional tasks
      // only
      const optionalTasks = computedGroups
        .flatMap((group) => group.tasks)
        .filter((task) => task.isOptional === true);
      const task = optionalTasks[parseInt(taskIndex)];

      // Then store the Task in the Ref
      taskRef.current = task;
      // And let the TaskReducer know about the Task
      dispatch({ type: "SET_TASK", payload: { task } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, subAttributes]);

  /*** Mutation ***/
  const { mutate: submitAnswers, error: submitError } = useMutation({
    mutationKey: ["submitAnswers"],
    // FIXME: Change the `identifier` to `attribute` in the API layer
    // to keep the response object fields consistent.
    mutationFn: (answers: POSTBody<"/api/sub/attributes">) => POST("/api/sub/attributes", answers),
    onSuccess: (newSubAttribute) => {
      // Update the cache with the new sub attributes
      QUERY_CLIENT.setQueryData(["attributes"], newSubAttribute);
      // Refresh the user in case they have `COMPLETED` the onboarding
      // requirements or have been `REJECTED` during the process.
      refreshUser();
    },
  });

  /***** Handlers *****/
  function handleSubmit(state: Parameters<PermitTaskPageReducerState["onSubmit"]>[0]) {
    return submitAnswers(
      // @ts-expect-error - @KoltonG This is having a hard time
      // handing the discriminant union type.
      state.unsyncedSubAttributes.map((usa) => ({
        identifier: usa.attribute,
        value: usa.value,
      })),
    );
  }

  function handleStepChange(newStepIndex: number) {
    const newUrl = generatePath(AUTH_URLS.permitTask, {
      task: taskIndex,
      step: newStepIndex,
    });

    replacePath(newUrl);
  }

  function handleClose() {
    history.push(createPermitURL());
  }

  /*** Render #1 - Invalidate Configuration or Sub Attributes ***/
  if (isSubAttributesError === true || isConfigurationError === true) {
    return (
      <ScorePage title={translate("PERMIT_PAGE_TITLE")}>
        <ContentSingleColumn>
          <InvalidConfiguration />
        </ContentSingleColumn>
      </ScorePage>
    );
  }

  /*** Render #2 - Loading ***/
  if (
    // Loading computed Task
    !state.taskComputed ||
    // Loading step index
    state.stepIndex?.current === undefined
  ) {
    return <ScorePage title={translate("PERMIT_PAGE_TITLE")} isLoading />;
  }

  /*** Render #3 ***/
  return (
    <ScorePage title={translate("PERMIT_PAGE_TITLE")}>
      <ContentSingleColumn>
        {submitError && <AlertError />}
        <PermitTaskPageBase
          taskComputed={state.taskComputed}
          stepIndex={state.stepIndex?.current}
          isLoading={state.isLoading}
          isReadOnly={state.isReadOnly}
          onAnswerChange={(subAttribute) =>
            dispatch({
              type: "UPDATE_ANSWER",
              payload: { subAttribute },
            })
          }
          onNextButtonClick={() => dispatch({ type: "NEXT_BUTTON" })}
          onPrevButtonClick={() => dispatch({ type: "PREV_BUTTON" })}
          onClose={handleClose}
        />
      </ContentSingleColumn>
    </ScorePage>
  );
}
